import React, { useId } from "react";
import { cn } from "./cn";

/**
 * Bun checkbox: 16px, radius 5, dim border when empty, ink fill when checked.
 * Renders a real <input type="checkbox"> for a11y with a styled box on top.
 */
export default function Checkbox({ checked, onChange, label, description, variant = 'default', disabled, readOnly = false, className, ...props }) {
  const descriptionId = useId();
  if (variant === 'card') {
    return (
      <label className={cn(
        'relative grid min-h-11 grid-cols-[minmax(0,1fr)_22px] items-center gap-x-3 rounded-bun-lg border-[1.5px] px-[14px] py-3 font-instrument sm:min-h-[118px] sm:grid-cols-[minmax(0,1fr)_18px] sm:items-start sm:gap-x-2 sm:p-4',
        checked ? 'border-bun-ink bg-bun-mist' : 'border-bun-line bg-bun-surface hover:border-bun-line-strong',
        disabled ? 'cursor-not-allowed opacity-60' : readOnly ? 'cursor-default' : 'cursor-pointer', className
      )}>
        <input {...props} type="checkbox" checked={checked} onChange={onChange} disabled={disabled || readOnly}
          aria-label={props['aria-label'] || label} aria-describedby={description ? descriptionId : undefined}
          className="peer absolute inset-0 h-full w-full cursor-[inherit] opacity-0" />
        <span aria-hidden="true" className="pointer-events-none absolute inset-0 rounded-bun-lg peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-bun-blue" />
        <span className="min-w-0">
          <span className="block text-bun-heading font-semibold leading-snug text-bun-text">{label}</span>
          {description && <span id={descriptionId} className={cn('mt-0.5 block text-bun-body font-book leading-relaxed sm:mt-2', checked ? 'text-bun-slate' : 'text-bun-stone')}>{description}</span>}
        </span>
        <span aria-hidden="true" className={cn(
          'inline-flex h-[22px] w-[22px] items-center justify-center rounded-full border-[1.5px] sm:h-[18px] sm:w-[18px]',
          checked ? 'border-bun-ink bg-bun-ink text-bun-on-ink' : 'border-bun-dim'
        )}>
          {checked && <svg className="h-3 w-3 sm:h-[10px] sm:w-[10px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M5 13l4 4L19 7" /></svg>}
        </span>
      </label>
    );
  }
  return (
    <label
      className={cn(
        "inline-flex min-h-11 min-w-11 sm:min-h-0 sm:min-w-0 items-center gap-[9px] font-instrument text-bun-body font-book text-bun-text",
        disabled ? "cursor-not-allowed opacity-60" : readOnly ? "cursor-default" : "cursor-pointer",
        className
      )}
    >
      <span className="relative inline-flex w-4 h-4 shrink-0">
        <input
          type="checkbox"
          checked={checked}
          onChange={onChange}
          disabled={disabled || readOnly}
          className="peer absolute inset-0 w-full h-full opacity-0 cursor-[inherit]"
          {...props}
        />
        <span
          aria-hidden="true"
          className={cn(
            "w-4 h-4 rounded-[5px] box-border inline-flex items-center justify-center",
            "border-[1.5px] border-bun-dim bg-transparent",
            "peer-checked:border-0 peer-checked:bg-bun-ink",
            "peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-bun-blue"
          )}
        >
          {checked && (
            <svg
              width="10"
              height="10"
              viewBox="0 0 24 24"
              fill="none"
              stroke="var(--bun-on-ink)"
              strokeWidth="3.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M5 13l4 4L19 7" />
            </svg>
          )}
        </span>
      </span>
      {label}
    </label>
  );
}
